import { AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createColumnHelper } from '@tanstack/react-table';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getServiceIcon } from '@/modules/onboarding/integrations-setup/helpers/data';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';

import { Client, ClientStatus } from '../types';

const columnHelper = createColumnHelper<Client>();

/**
 * Type guard to check if a string is a valid IntegrationServices enum value
 */
const isValidIntegrationService = (type: string): type is IntegrationServices => {
  return Object.values(IntegrationServices).includes(type as IntegrationServices);
};

/**
 * Helper function to render text with opacity for archived clients
 */
const renderArchivedText = (status: ClientStatus | undefined, value?: string) => (
  <ScrollableText
    className={cn('whitespace-nowrap', status === ClientStatus.Archived && 'opacity-50')}
  >
    {value || ''}
  </ScrollableText>
);

export const getClientTableColumns = () => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-3">
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectAll')}
        />
      </label>
    ),
    cell: ({ row }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-4">
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectRow')}
        />
      </label>
    ),
    enableSorting: false,
    enableColumnFilter: false,
    size: 40,
    minSize: 40,
    maxSize: 40,
  }),
  columnHelper.accessor('name', {
    header: i18n.t('authenticated.clients:headers.clientName'),
    cell: ({ row, getValue }) => (
      <ScrollableCellWithAvatar
        text={getValue() || i18n.t('common:table.noValue')}
        image={row.original?.logo}
        avatarName={getValue()}
        className={cn(row.original.status === ClientStatus.Archived && 'opacity-50')}
      />
    ),
    size: 150,
    enablePinning: true,
    enableHiding: false,
  }),
  columnHelper.accessor('email', {
    header: i18n.t('authenticated.clients:headers.email'),
    size: 200,
    cell: ({ getValue, row }) =>
      renderArchivedText(row.original.status, getValue() || i18n.t('common:table.noValue')),
  }),

  columnHelper.accessor('manager', {
    header: i18n.t('authenticated.clients:headers.manager'),
    cell: ({ getValue, row }) => {
      const manager = getValue();
      return manager?.name ? (
        <ScrollableCellWithAvatar
          text={manager.name}
          image={manager.avatar}
          avatarName={manager.name}
          className={cn(row.original.status === ClientStatus.Archived && 'opacity-50')}
        />
      ) : (
        renderArchivedText(row.original.status, i18n.t('common:table.noValue'))
      );
    },
    size: 200,
  }),
  columnHelper.accessor('status', {
    header: i18n.t('authenticated.clients:headers.status'),
    enableHiding: false,
    cell: ({ getValue }) => {
      const status = getValue();

      return (
        <StatusBadge
          type={StatusBadgeType.Client}
          status={status}
          label={i18n.t(`authenticated.clients:status.${status}`)}
        />
      );
    },
    size: 200,
  }),
  columnHelper.accessor('paymentTerms', {
    header: i18n.t('authenticated.clients:headers.paymentTerms'),
    size: 200,
    cell: ({ getValue, row }) =>
      renderArchivedText(
        row.original.status,
        i18n.t(`authenticated.clients:paymentTerms.${getValue()}`)
      ),
  }),
  columnHelper.accessor('integrations', {
    header: i18n.t('authenticated.clients:headers.integrations'),
    cell: ({ getValue, row }) => {
      const integrations = getValue() || [];

      if (integrations.length === 0) {
        return renderArchivedText(
          row.original.status,
          i18n.t('authenticated.clients:table.noConnections')
        );
      }

      return (
        <div className="flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale">
          {integrations.map(integration => {
            const iconSrc =
              integration.icon ||
              (isValidIntegrationService(integration.type)
                ? getServiceIcon(integration.type)
                : undefined);

            return (
              <SimpleTooltip key={integration.id} content={integration.type}>
                <Avatar slot="avatar" className="flex h-6 w-6 items-center justify-center bg-muted">
                  <AvatarImage src={iconSrc} alt={integration.type} className="h-4 w-4" />
                  <AvatarFallback>{integration.type.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
              </SimpleTooltip>
            );
          })}
        </div>
      );
    },
  }),
];
